import * as d3 from 'd3';
import Moment from 'moment';

export class MultiLineD3Chart {
  constructor(element, data, yAxisTitle = 'Register Data', reportUnit, lineColorObj, name) {
    // load in arguments from config object
    this.data = this.restructureData(data);
    this.element = element;
    this.yAxisTitle = yAxisTitle;
    this.reportUnit = reportUnit;
    this.lineColorObj = lineColorObj;
    this.name = name;
    this.lineColor = {
      line0: '#BBBBBB',
      line1: '#EE6677',
      line2: '#66CCEE',
      line3: '#AA3377',
      line4: '#CCBB44',
      line5: '#4477AA',
      line6: '#228833',
    };
    window.addEventListener('resize', (e) => this.draw(e));
    // create the chart
    this.draw();
  }

  draw() {
    // Define width
    this.width = this.element.offsetWidth;

    // Set height to min value of 450
    this.height = this.width < 390 ? 450 : 490;

    if (this.width < 400) {
      this.margin = {
        top: 20,
        bottom: 75,
        left: 45,
        right: 30,
      };
    } else {
      this.margin = {
        top: 20,
        bottom: 75,
        left: 65,
        right: 30,
      };
    }

    this.svgHeight = this.height - (this.margin.top + this.margin.bottom);
    this.svgWidth = this.width <= 0 ? 0 : this.width - (this.margin.left + this.margin.right);

    // set up parent element and SVG
    this.element.innerHTML = '';
    const svg = d3.select(this.element).append('svg');
    svg.attr('width', this.width);
    svg.attr('height', this.height);
    svg.attr('id', 'line-graph');

    // we'll actually be appending to a <g> element
    this.plot = svg.append('g').attr('transform', `translate(${this.margin.left},${this.margin.top})`);
    if (this.data.length > 0) {
      this.createScales();
      this.addAxes();
      this.addGrid();
      this.addLines();
      this.addTooltip(this.xScale, this.yScale, this.data, this.yAxisTitle, this.reportUnit, this.lineColor);
    }
  }

  update(data, unit, reportUnit, lineColorObj) {
    this.data = this.restructureData(data);
    this.lineColorObj = lineColorObj;
    this.yAxisTitle = unit;
    this.reportUnit = reportUnit;
    // draw the rest of the graph
    this.draw();
  }

  restructureData(data) {
    const keys = Object.keys(data);

    const dataArray = [];
    let longest = 0;
    let longestData = [];

    // create initial object with all keys set to null
    const initializeRegisters = keys.reduce((o, key) => ({ ...o, [key]: null }), {});

    // find the register with the most data points
    keys.forEach((reg) => {
      if (data[reg].length > longest) {
        longestData = data[reg];
        longest = data[reg].length;
      }
    });

    // add initialized registers with null data, longest data set and dates to dataArray
    longestData.forEach((dataset) => {
      dataArray.push({
        ...initializeRegisters,
        ...dataset,
      });
    });

    // go through the registers and replace null values if applicable
    keys.forEach((register) => {
      const values = data[register];
      values.forEach((dataset, index) => {
        if (data[register].length === longest) {
          // If the data length is the same as the longest data length
          // ie. there is no missing data
          dataArray[index][register] = dataset[register];
        } else {
          const indexOfDate = dataArray.findIndex((element) => {
            const { _i: elDateFormat } = element.date;
            const { _i: datasetDateFormat } = dataset.date;
            return elDateFormat === datasetDateFormat;
          });
          dataArray[indexOfDate][register] = dataset[register];
        }
      });
    });
    return dataArray;
  }

  createScales() {
    // shorthand to save typing later
    const m = this.margin;

    // calculate max and min for data
    const xExtent = d3.extent(this.data, (d) => d.date);

    // make y-axis bigger than max value
    const smallest = d3.min(this.data, (d) => {
      const datum = JSON.parse(JSON.stringify(d));
      delete datum.date;
      return Math.min(...d3.values(datum));
    });
    const largest = d3.max(this.data, (d) => {
      const datum = JSON.parse(JSON.stringify(d));
      delete datum.date;
      return Math.max(...d3.values(datum));
    });
    const yExtent = [smallest * 1.1, largest * 1.1];

    // force zero baseline if all data points are positive
    if (yExtent[0] > 0) {
      yExtent[0] = 0;
    }

    // force the upper limit of range to be 1 if less than 1
    // otherwise D3 will change the scaling to use milli
    if (yExtent[1] < 1) {
      yExtent[1] = 1.0;
    }

    this.xScale = d3
      .scaleTime()
      .range([0, this.svgWidth])
      .domain(xExtent);

    this.yScale = d3
      .scaleLinear()
      .range([this.height - (m.top + m.bottom), 0])
      .domain(yExtent);
  }

  addAxes() {
    const m = this.margin;

    const xAxis = d3.axisBottom().scale(this.xScale);

    if (this.width < 700 && this.reportUnit === 'month') {
      xAxis.ticks(d3.timeMonth.every(2)).tickFormat(d3.timeFormat('%b'));
    } else if (this.width < 700 && this.reportUnit === 'day') {
      xAxis.ticks(6);
    } else if (this.width < 700 && this.reportUnit === 'hour') {
      xAxis.ticks(4);
    }
    if (this.data.length < 10 && this.reportUnit === 'day') {
      xAxis.ticks(this.data.length);
    }

    const startDate = Moment(this.data[0].date).format('MMM DD');
    const endDate = Moment(this.data[this.data.length - 1].date).format('MMM DD');
    const xAxisTitle = `${startDate} - ${endDate}`;

    const yAxis = d3
      .axisLeft()
      .scale(this.yScale)
      .ticks(10, '~s');

    this.plot
      .append('g')
      .style('font-size', '10px')
      .attr('class', 'x axis')
      .attr('transform', `translate(0, ${this.height - (m.top + m.bottom)})`)
      .call(xAxis)
      .selectAll('text')
      .attr('y', 22)
      .attr('x', -28)
      .attr('dy', '.25em')
      .attr('transform', 'rotate(-25)')
      .style('text-anchor', 'start');

    this.plot
      .append('text')
      .attr('class', 'axisTitle')
      .attr('text-anchor', 'middle') // this makes it easy to centre the text as the transform is applied to the anchor
      .attr(
        'transform',
        `translate(${this.svgWidth * 0.5}, ${this.height - (this.margin.top + this.margin.bottom / 3)})`,
      )
      // centre below axis
      .text(xAxisTitle);

    this.plot
      .append('g')
      .style('font-size', '11px')
      .attr('class', 'y axis')
      .call(yAxis)
      .select('.domain')
      .remove()
      .selectAll('.tick text')
      .attr('x', 4)
      .attr('dy', -4);

    if (this.width > 399) {
      this.plot
        .append('text')
        .attr('class', 'axisTitle')
        // this makes it easy to centre the text as the transform is applied to the anchor
        .attr('text-anchor', 'middle')
        // text is drawn off the screen top left, move down and out and rotate
        .attr('transform', `translate(${-this.margin.left / 1.5},${this.svgHeight / 2})rotate(-90)`)
        .text('Status');
    }

    if (this.width < 700) {
      this.plot.style('font-size', '13px');
    }
  }

  addGrid() {
    const yGrid = d3
      .axisLeft()
      .scale(this.yScale)
      .tickSize(-this.svgWidth)
      .tickFormat('')
      .ticks(8);

    // add the Y gridlines
    this.plot
      .append('g')
      .attr('class', 'grid')
      .call(yGrid)
      .select('.domain')
      .remove();
  }

  addLines() {
    const keys = Object.keys(this.lineColorObj);
    for (let i = 0; i <= keys.length - 1; i += 1) {
      const dataLine = d3
        .line()
        .defined((d) => d[Object.keys(d)[i]] !== null)
        .x((d) => this.xScale(d.date))
        .y((d) => this.yScale(+d[keys[i]]));
      this.addLine(dataLine, `${this.lineColorObj[keys[i]]}`);
    }
  }

  addLine(line, color, style = []) {
    this.plot
      .append('path')
      // use data stored in `this`
      .datum(this.data)
      .classed('line', true)
      .attr('d', line)
      // set stroke to specified color, or default to red
      .style('stroke', this.lineColor[color] || 'ffffff')
      .style(style[0], style[1]);
  }

  addTooltip(xScale, yScale, data, unit, reportUnit, lineColor) {
    const keys = Object.keys(this.lineColorObj);
    const focus = this.plot
      .append('g')
      .attr('id', 'focus')
      .attr('class', 'focus')
      .style('display', 'none');

    focus
      .append('line')
      .attr('class', 'x-hover-line hover-line')
      .attr('y1', 0)
      .attr('y2', this.svgHeight);

    for (let i = 0; i <= keys.length - 1; i += 1) {
      focus
        .append('circle')
        .attr('class', `circle--${this.lineColorObj[keys[i]]}`)
        .attr('r', '6');
    }

    const tooltip = d3
      .select('.line-graph')
      .append('div')
      .attr('class', 'multiline-tooltip')
      .style('opacity', 0);

    function bisectDate(dateArray, mouseDate) {
      return d3.bisectLeft(dateArray, mouseDate);
    }

    function findYValueFromDate(selectedDate) {
      for (let i = 0; i < data.length; i += 1) {
        if (data[i].date === selectedDate) {
          return data[i];
        }
      }
      return false;
    }

    function moveTooltip(d, index, nodes, lineColorObj, name, touch = '') {
      d3.select('#focus').style('display', null);
      let mousePositionX = 0;
      let mousePositionY = 0;
      let bisectedDate;
      let tooltipDate;
      let mousePosition = [0, 0];
      const lineGraph = document.getElementById('line-graph');
      const { width, height } = lineGraph.getBoundingClientRect();

      if (touch) {
        [mousePosition] = d3.touches(nodes[index]);
        [mousePositionX, mousePositionY] = mousePosition;
      } else {
        [mousePositionX, mousePositionY] = d3.mouse(nodes[index]);
      }
      const date = xScale.invert(mousePositionX);
      const dateArray = data.map((el) => el.date);
      const i = bisectDate(dateArray, date);

      if (i === 0) {
        [bisectedDate] = dateArray;
      } else {
        const d0 = dateArray[i - 1];
        const d1 = dateArray[i];
        bisectedDate = date - d0 > d1 - date ? d1 : d0;
      }
      switch (reportUnit.toString()) {
        case 'month':
          tooltipDate = Moment(bisectedDate).format('MMMM');
          break;
        case 'day':
          tooltipDate = Moment(bisectedDate).format('MMMM Do');
          break;
        case 'hour':
          tooltipDate = Moment(bisectedDate).format('MMMM Do hA');
          break;
        case 'fifteen':
        case 'five':
          tooltipDate = Moment(bisectedDate).format('Do hh:mm A');
          break;
        case 'minute':
          tooltipDate = Moment(bisectedDate).format('Do hh:mm A');
          break;
        default:
          break;
      }

      const yValue = findYValueFromDate(bisectedDate);
      let tooltipHTML = `
        <div style="font-weight: 600; margin-bottom: 1rem;">${tooltipDate}</div>
        `;
      if (window.innerWidth > 599) {
        tooltipHTML += '<div class="container">';
      }
      tooltipHTML += '<ul>';
      for (let j = 0; j <= keys.length - 1; j += 1) {
        const deg = unit[j] === 'C' ? String.fromCharCode(176) : '';
        let legendValue;
        try {
          legendValue = yValue[keys[j]].toFixed(3);
        } catch (err) {
          legendValue = 'N/A';
        }
        tooltipHTML += `
          <div class="tooltip__item">
            <div class="color-box" style="background-color: ${lineColor[lineColorObj[keys[j]]]};"></div>
            <div class="tooltip__text--baseline">
              ${yValue[keys[j]] === null ? 'N/A' : `${legendValue} ${deg}${unit[keys[j]]}`}
            </div>
          </div>
        `;
      }
      tooltipHTML += '</ul>';
      if (window.innerWidth > 599) {
        tooltipHTML += '</div>';
      }

      d3.select('.line-graph')
        .select('.multiline-tooltip')
        .style('opacity', 0.9)
        .style('display', 'block')
        .html(tooltipHTML);

      if (touch) {
        // Dragging across the graph

        // Handle cases where the cursor is beyond the boundaries of chart
        if (mousePositionX < 0) {
          mousePositionX = 0;
        } else if (mousePositionX > width - 80) {
          mousePositionX = width - 80;
        }

        if (mousePositionY < 0) {
          mousePositionY = 0;
        } else if (mousePositionY > height) {
          mousePositionY = height;
        }

        if (window.innerWidth < 599) {
          if (name === 'Thermostat') {
            mousePositionY = 350;
          } else {
            mousePositionY = 170;
          }
        }

        // If there's not enough space to fit the tooltip on the right side
        if (mousePositionX > width - 300) {
          if (window.innerWidth > window.innerHeight) {
            // If the current device is wider than it is long (ie. landscaped mobile)
            if (window.innerWidth < 599) {
              d3.select('.line-graph')
                .select('.multiline-tooltip')
                .style('left', `${mousePositionX - 75}px`)
                .style('top', `${mousePositionY - 30}`);
            } else {
              d3.select('.line-graph')
                .select('.multiline-tooltip')
                .style('left', `${mousePositionX + 95}px`)
                .style('top', `${mousePositionY - 30}`);
            }
          } else {
            // If the device screen is longer than it is wide
            /* eslint-disable-next-line */
            if (window.innerWidth < 599) {
              d3.select('.line-graph')
                .select('.multiline-tooltip')
                .style('top', `${mousePositionY - 30}px`)
                .style('left', `${mousePositionX - 60}px`);
            } else {
              d3.select('.line-graph')
                .style('top', `${mousePositionY - 30}px`)
                .select('.multiline-tooltip')
                .style('left', `${mousePositionX + 95}px`);
            }
          }
        } else if (mousePositionX <= width - 300) {
          // Else if there is enough space to fit the tooltip on the right
          if (window.innerWidth > window.innerHeight) {
            if (window.innerWidth < 599) {
              d3.select('.line-graph')
                .select('.multiline-tooltip')
                .style('top', `${mousePositionY - 30}`)
                .style('left', `${mousePositionX + 80}px`);
            } else {
              d3.select('.line-graph')
                .select('.multiline-tooltip')
                .style('top', `${mousePositionY - 30}`)
                .style('left', `${mousePositionX + 340}px`);
            }
          } else {
            /* eslint-disable-next-line */
            if (window.innerWidth < 599) {
              d3.select('.line-graph')
                .select('.multiline-tooltip')
                .style('top', `${mousePositionY - 30}px`)
                .style('left', `${mousePositionX + 60}px`);
            } else {
              d3.select('.line-graph')
                .select('.multiline-tooltip')
                .style('top', `${mousePositionY - 30}px`)
                .style('left', `${mousePositionX + 340}px`);
            }
          }
        }
      } else if (!touch) {
        // Hovering
        if (window.innerWidth < 599) {
          if (name === 'Thermostat') {
            mousePositionY = 350;
          } else {
            mousePositionY = 170;
          }
        }

        if (mousePositionX > width - 200) {
          // If there's not enough space to fit tooltip on the right of cursor
          if (window.innerWidth < 599) {
            d3.select('.line-graph')
              .select('.multiline-tooltip')
              .style('top', `${mousePositionY - 30}px`)
              .style('left', `${d3.event.pageX - 130}px`);
          } else {
            d3.select('.line-graph')
              .select('.multiline-tooltip')
              .style('top', `${mousePositionY - 30}px`)
              .style('left', `${d3.event.pageX - 230}px`);
          }
        } else {
          d3.select('.line-graph')
            .select('.multiline-tooltip')
            .style('top', `${mousePositionY - 30}px`)
            .style('left', `${d3.event.pageX + 15}px`);
        }
      }

      d3.select('#focus').attr('transform', `translate(${xScale(bisectedDate)}, 0)`);

      for (let j = 0; j <= keys.length - 1; j += 1) {
        if (yValue[keys[j]] !== 'N/A') {
          d3.select('#focus')
            .select(`.circle--${lineColorObj[keys[j]]}`)
            .attr('cy', `${yScale(yValue[keys[j]])}`);
        } else {
          d3.select('#focus')
            .select(`.circle--${lineColorObj[keys[j]]}`)
            .style('opacity', 0);
        }
      }
    }

    this.plot
      .append('rect')
      .attr('id', 'overlay')
      .attr('class', 'overlay')
      .attr('width', this.svgWidth)
      .attr('height', this.svgHeight)
      .on('mouseover', () => {
        focus.style('display', null);
      })
      .on('mouseout touchend', () => {
        focus.style('display', 'none');
        tooltip.style('display', 'none');
      })
      .on('mousemove', (d, index, nodes) => {
        moveTooltip(d, index, nodes, this.lineColorObj, this.name);
      })
      .on('touchmove', (d, index, nodes) => {
        moveTooltip(d, index, nodes, this.lineColorObj, this.name, 'touch');
      });
  }
}
